import { xchacha20poly1305 } from '@noble/ciphers/chacha';
import { sha256 } from '@noble/hashes/sha2';
import { b64dec, b64enc, concat, fromUtf8, utf8 } from './bytes.js';
import { gzipCompress, gzipDecompress } from './gzip.js';
import { roundForUnlock, getChainHash, tlockWrapCek, tlockUnwrapCek } from './tlock.js';
import { wrapX25519, unwrapX25519 } from './wrap.js';

export type Policy = 'time' | 'manual' | 'time_manual';

export type TlckHeader = {
  v: 1;
  policy: Policy;
  lock_id?: string;
  unlock_unix?: number;
  drand_round?: number;
  drand_chain?: string;
  orig_name: string;
  orig_mime: string;
  body_nonce: string;
  wrap_tlock?: string;
  wrap_x25519?: string;
};

const PREAMBLE =
  '🔒 TimeLock versleuteld bestand\n' +
  'Dit bestand is tijdgebonden versleuteld. Open met TimeLock om te ontsleutelen.\n' +
  'Meer info: https://timelock.whippet.io\n\n';
const SEPARATOR = '---TLCK---';
const CHECKSUM_DOMAIN = utf8('tlck-corruption-v1:');

/**
 * Plain SHA-256 over a domain string and the gzipped payload. This is a
 * **corruption tripwire**, not a tamper guard: an attacker who modifies the
 * payload simply recomputes this tag. Real authentication of the ciphertext is
 * provided by the inner XChaCha20-Poly1305 AEAD (and, for manual policies, by
 * the X25519-wrap AEAD on the CEK). The point of this hash is to fail fast on
 * disk/network corruption *before* attempting decryption.
 *
 * (An earlier version called this an HMAC. It used a key derived from the
 * payload itself, which makes the HMAC machinery indistinguishable from a plain
 * hash — and the "anti-tampering" claim that came with it was misleading.)
 */
function corruptionTag(payload: Uint8Array): Uint8Array {
  const h = sha256.create();
  h.update(CHECKSUM_DOMAIN);
  h.update(payload);
  return h.digest();
}

export type EncryptInput = {
  fileBytes: Uint8Array;
  fileName: string;
  fileMime: string;
  policy: Policy;
  unlockUnix?: number;
  lockId?: Uint8Array;
  releasePubkey?: Uint8Array;
};

export type EncryptOutput = {
  tlckBytes: Uint8Array;
  header: TlckHeader;
};

export async function encryptToTlck(input: EncryptInput): Promise<EncryptOutput> {
  const cek = crypto.getRandomValues(new Uint8Array(32));
  const bodyNonce = crypto.getRandomValues(new Uint8Array(24));
  const aead = xchacha20poly1305(cek, bodyNonce);
  const body = aead.encrypt(input.fileBytes);

  const header: TlckHeader = {
    v: 1,
    policy: input.policy,
    orig_name: input.fileName,
    orig_mime: input.fileMime,
    body_nonce: b64enc(bodyNonce),
  };

  if (input.policy === 'time' || input.policy === 'time_manual') {
    if (typeof input.unlockUnix !== 'number') throw new Error('unlockUnix required');
    const round = await roundForUnlock(input.unlockUnix);
    const chain = await getChainHash();
    header.unlock_unix = input.unlockUnix;
    header.drand_round = round;
    header.drand_chain = chain;
    header.wrap_tlock = await tlockWrapCek(cek, round);
  }

  if (input.policy === 'manual' || input.policy === 'time_manual') {
    if (!input.lockId) throw new Error('lockId required');
    if (!input.releasePubkey) throw new Error('releasePubkey required');
    header.lock_id = toHex(input.lockId);
    header.wrap_x25519 = b64enc(wrapX25519(cek, input.releasePubkey));
  }

  const headerJson = utf8(JSON.stringify(header));
  const payload = concat(headerJson, utf8('\n'), utf8(SEPARATOR), utf8('\n'), body);
  const gzPayload = await gzipCompress(payload);

  const tag = corruptionTag(gzPayload);

  const preamble = utf8(PREAMBLE);
  const tlckBytes = concat(preamble, tag, gzPayload);
  return { tlckBytes, header };
}

function toHex(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) s += b[i].toString(16).padStart(2, '0');
  return s;
}

export type ParsedTlck = {
  header: TlckHeader;
  body: Uint8Array;
};

export async function parseTlck(file: Uint8Array): Promise<ParsedTlck> {
  const preamble = utf8(PREAMBLE);
  if (file.length < preamble.length + 32) throw new Error('file_too_small');

  // Find the preamble by searching for the double-newline terminator rather than
  // relying on exact byte match (future-proof if preamble text gains newlines).
  const head = file.subarray(0, Math.min(file.length, 1024));
  let sepIndex = -1;
  for (let i = 0; i < head.length - 1; i++) {
    if (head[i] === 0x0a && head[i + 1] === 0x0a) {
      sepIndex = i + 2;
      break;
    }
  }
  if (sepIndex < 0) throw new Error('no_valid_preamble');

  const afterPreamble = file.subarray(sepIndex);
  if (afterPreamble.length < 32 + 2) throw new Error('file_corrupted');

  const tag = afterPreamble.subarray(0, 32);
  const gzPayload = afterPreamble.subarray(32);

  const expected = corruptionTag(gzPayload);
  if (!constantTimeEq(tag, expected)) throw new Error('file_corrupted');

  const payload = await gzipDecompress(gzPayload);

  // Find the JSON header: payload starts with JSON text, followed by \n---TLCK---\n, then body.
  const needle = utf8('\n' + SEPARATOR + '\n');
  const idx = findSeq(payload, needle);
  if (idx < 0) throw new Error('no_separator');

  const headerJson = fromUtf8(payload.subarray(0, idx));
  const body = payload.subarray(idx + needle.length);

  let header: TlckHeader;
  try {
    header = JSON.parse(headerJson) as TlckHeader;
  } catch {
    throw new Error('header_unreadable');
  }
  if (header.v !== 1) throw new Error('unknown_version');

  return { header, body };
}

function findSeq(haystack: Uint8Array, needle: Uint8Array): number {
  outer: for (let i = 0; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

function constantTimeEq(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

export type DecryptInput = {
  parsed: ParsedTlck;
  /** For manual / time+manual: the X25519 release privkey (from the server). */
  releasePriv?: Uint8Array;
};

export async function decryptFromTlck(input: DecryptInput): Promise<Uint8Array> {
  const { header, body } = input.parsed;

  const cek = await recoverCek(header, input.releasePriv);
  if (!cek) throw new Error('no_key_available');

  const nonce = b64dec(header.body_nonce);
  const aead = xchacha20poly1305(cek, nonce);
  const plaintext = aead.decrypt(body);
  return plaintext;
}

async function recoverCek(
  header: TlckHeader,
  releasePriv: Uint8Array | undefined,
): Promise<Uint8Array | null> {
  // Prefer X25519 when we have a release key (time+manual released early).
  if (releasePriv && header.wrap_x25519) {
    const unwrapped = unwrapX25519(b64dec(header.wrap_x25519), releasePriv);
    if (unwrapped) return unwrapped;
  }
  if (header.wrap_tlock) {
    try {
      return await tlockUnwrapCek(header.wrap_tlock);
    } catch {
      /* drand round may not yet be available */
    }
  }
  return null;
}

export type QueueState = 'waiting' | 'waiting-manual' | 'ready' | 'decrypting' | 'done' | 'error';

export function initialStateFor(header: TlckHeader): QueueState {
  if (header.policy === 'manual') return 'waiting-manual';
  if (header.policy === 'time') {
    return (header.unlock_unix ?? 0) * 1000 <= Date.now() ? 'ready' : 'waiting';
  }
  // time_manual
  return (header.unlock_unix ?? 0) * 1000 <= Date.now() ? 'ready' : 'waiting';
}
